import cx from "classnames"
import { navigate } from "gatsby"
import PropTypes from "prop-types"
import React from "react"
import Link from "components/Link"
import { trackEvent } from "lib/googleTagManager"
import ArrowIcon from "./arrow.inline.svg"
import * as css from "./Button.module.css"

function hrefIsRelative(href) {
  return href.startsWith("/")
}

function Button({
  href,
  onClick,
  children,
  arrow,
  color,
  primary,
  text,
  link,
  rounded,
  className,
  target,
  rel,
  small,
  regular,
  long,
  large,
  exlarge,
  trackClick,
  showBlueBorder,
  ...rest
}) {
  const effectiveColor = primary && !color ? "green" : color
  const classNames = cx(
    css.button,
    {
      [css.text]: text && !link,
      [css.link]: link,
      [css.rounded]: rounded,
      [css.colorAzure]: effectiveColor === "azure",
      [css.colorBlue]: effectiveColor === "blue",
      [css.colorGreen]: effectiveColor === "green",
      [css.colorWhite]: effectiveColor === "white",
      [css.colorTransparent]: effectiveColor === "transparent",
      [css.colorLightBlue]: effectiveColor === "lightBlue",
      [css.colorGradientSeafoam]: effectiveColor === "gradientSeafoam",
      [css.small]: small,
      [css.regular]: regular,
      [css.large]: large,
      [css.exlarge]: exlarge,
      [css.long]: long,
      [css.blueBorder]: showBlueBorder,
    },
    className
  )

  const handleClick = (e) => {
    if (href && hrefIsRelative(href)) {
      navigate(href)
      e.preventDefault()
    }
    if (trackClick) {
      trackEvent("buttonClick", { buttonClickEventName: trackClick })
    }
    if (onClick) {
      onClick(e)
    }
  }

  if (href) {
    const isRelative = hrefIsRelative(href)
    const Component = isRelative ? Link : "a"
    return (
      <Component
        href={href}
        onClick={handleClick}
        className={classNames}
        rel={rel || (target === "_blank" ? "noopener" : null)}
        target={target}
        {...rest}
      >
        {children}
        {arrow && <ArrowIcon className={css.arrow} />}
      </Component>
    )
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={classNames}
      {...rest}
    >
      {children}
      {arrow && <ArrowIcon className={css.arrow} />}
    </button>
  )
}

Button.propTypes = {
  href: PropTypes.string,
  onClick: PropTypes.func,
  arrow: PropTypes.bool,
  color: PropTypes.oneOf([
    "azure",
    "blue",
    "green",
    "white",
    "transparent",
    "lightBlue",
    "gradientSeafoam",
  ]),
  /** Shorthand for color="green" — matches legacy Semantic UI primary button */
  primary: PropTypes.bool,
  text: PropTypes.bool,
  link: PropTypes.bool,
  rounded: PropTypes.bool,
  small: PropTypes.bool,
  regular: PropTypes.bool,
  large: PropTypes.bool,
  exlarge: PropTypes.bool,
  long: PropTypes.bool,
  className: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  trackClick: PropTypes.string,
  children: PropTypes.node,
}

export default Button
